import type { JSONContent } from "@tiptap/core";
import type { PostgrestError } from "@supabase/supabase-js";
import { loreEntryTypes } from "@/types/lore";
import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  buildLoreDocumentBody,
  buildProjectDocumentView,
  cloneEmptyEditorDocument,
  mapLoreCapacityRow,
  mapLoreEntryRecordToView,
  parseLoreLimitError,
  sortLoreEntriesByUpdatedAt,
} from "@/features/lore/mappers/loreMappers";
import {
  createLoreEntrySchema,
  projectDocumentSchema,
  updateLoreEntrySchema,
} from "@/features/lore/validation/loreSchemas";
import {
  createLoreCoverSignedUrl,
  deleteLoreCover,
  uploadLoreCover,
} from "@/features/lore/api/loreStorage";
import type {
  CreateLoreEntryInput,
  GovernanceDocRecord,
  LoreCanonStatus,
  LoreEntryFilters,
  LoreEntryRecord,
  LoreEntryView,
  ProjectDocumentKind,
  ProjectDocumentView,
  ProjectLoreCapacitySummary,
  StorySpineRecord,
  UpdateLoreEntryInput,
  UpdateProjectDocumentInput,
} from "@/types/lore";

export type LoreActionErrorCode =
  | "generic"
  | "missing-config"
  | "access-denied"
  | "not-found"
  | "lore-limit-reached";

interface LoreActionErrorOptions {
  cause?: unknown;
  limitPrompt?: ReturnType<typeof parseLoreLimitError>;
}

export class LoreActionError extends Error {
  readonly cause?: unknown;
  readonly limitPrompt?: ReturnType<typeof parseLoreLimitError>;

  constructor(
    public readonly code: LoreActionErrorCode,
    message?: string,
    options?: LoreActionErrorOptions,
  ) {
    super(message ?? code);
    this.name = "LoreActionError";
    this.cause = options?.cause;
    this.limitPrompt = options?.limitPrompt;
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new LoreActionError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

async function getCurrentUserId() {
  ensureConfigured();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new LoreActionError("access-denied", "No authenticated user available.");
  }

  return user.id;
}

function mapLoreError(error: unknown): LoreActionError {
  if (error instanceof LoreActionError) {
    return error;
  }

  const message =
    typeof error === "object" && error && "message" in error
      ? String(error.message)
      : "Lore action failed.";

  const limitPrompt = parseLoreLimitError(message);
  if (limitPrompt) {
    return new LoreActionError(
      "lore-limit-reached",
      "Lore entry limit reached for this project.",
      {
        cause: error,
        limitPrompt,
      },
    );
  }

  const postgrestError = error as PostgrestError | undefined;
  if (postgrestError?.code === "PGRST116") {
    return new LoreActionError("not-found", "That lore record could not be found.", {
      cause: error,
    });
  }

  if (postgrestError?.code === "42501" || message.toLowerCase().includes("row-level security")) {
    return new LoreActionError("access-denied", "You do not have access to that project.", {
      cause: error,
    });
  }

  return new LoreActionError("generic", message, { cause: error });
}

export function formatLoreErrorMessage(error: unknown) {
  const loreError = mapLoreError(error);

  switch (loreError.code) {
    case "missing-config":
      return "lore.messages.missingConfig";
    case "access-denied":
      return "lore.messages.accessDenied";
    case "not-found":
      return "lore.messages.notFound";
    case "lore-limit-reached":
      return "lore.messages.limitReached";
    default:
      return "lore.messages.saveError";
  }
}

async function getLoreCoverUrl(coverImagePath: string | null) {
  if (!coverImagePath) {
    return null;
  }

  try {
    return await createLoreCoverSignedUrl(coverImagePath);
  } catch {
    return null;
  }
}

async function mapLoreEntryWithCover(record: LoreEntryRecord) {
  const coverImageUrl = await getLoreCoverUrl(record.cover_image_path);
  return mapLoreEntryRecordToView(record, coverImageUrl);
}

function isJsonContent(value: unknown): value is JSONContent {
  return Boolean(value) && typeof value === "object" && "type" in (value as Record<string, unknown>);
}

function normalizeImportedLoreBody(value: unknown) {
  if (isJsonContent(value)) {
    return value;
  }

  const text = typeof value === "string" ? value.trim() : "";
  if (!text) {
    return cloneEmptyEditorDocument();
  }

  return {
    content: [{
      content: [{
        text,
        type: "text",
      }],
      type: "paragraph",
    }],
    type: "doc",
  } satisfies JSONContent;
}

export function buildImportedLoreEntryDraft(input: {
  displayTitle: string;
  elementData: Record<string, unknown>;
  elementType: "location" | "world_system";
}): CreateLoreEntryInput {
  const inferredType =
    input.elementType === "location"
      ? "location"
      : loreEntryTypes.includes(input.elementData.loreType as (typeof loreEntryTypes)[number])
        ? input.elementData.loreType as (typeof loreEntryTypes)[number]
        : "culture";

  return {
    body: normalizeImportedLoreBody(input.elementData.body ?? input.elementData.summary),
    localRules: cloneEmptyEditorDocument(),
    title:
      typeof input.elementData.title === "string" && input.elementData.title.trim().length > 0
        ? input.elementData.title
        : input.displayTitle,
    type: inferredType,
  };
}

export function buildImportedLoreElementData(
  input: CreateLoreEntryInput,
  baseElementData: Record<string, unknown> = {},
) {
  return {
    ...baseElementData,
    body: buildLoreDocumentBody(input.type, input.body, input.localRules),
    loreType: input.type,
    title: input.title,
  };
}

export function buildImportedProjectDocumentView(
  kind: ProjectDocumentKind,
  body: JSONContent,
): ProjectDocumentView {
  return buildProjectDocumentView(kind, {
    body,
    id: `import-review-${kind}`,
    project_id: "",
    updated_at: new Date().toISOString(),
  } as GovernanceDocRecord | StorySpineRecord);
}

export function buildImportedProjectDocumentElementData(
  input: UpdateProjectDocumentInput,
  baseElementData: Record<string, unknown> = {},
) {
  return {
    ...baseElementData,
    body: input.body,
    summary: "",
  };
}

async function getOwnedLoreEntryById(entryId: string) {
  ensureConfigured();

  const { data, error } = await supabase
    .from("lore_entries")
    .select("*")
    .eq("id", entryId)
    .maybeSingle();

  if (error) {
    throw mapLoreError(error);
  }

  return (data as LoreEntryRecord | null) ?? null;
}

export async function loadProjectDocuments(projectId: string): Promise<ProjectDocumentView[]> {
  ensureConfigured();

  const [governanceResult, storySpineResult] = await Promise.all([
    supabase
      .from("governance_docs")
      .select("*")
      .eq("project_id", projectId)
      .maybeSingle(),
    supabase
      .from("story_spines")
      .select("*")
      .eq("project_id", projectId)
      .maybeSingle(),
  ]);

  if (governanceResult.error) {
    throw mapLoreError(governanceResult.error);
  }

  if (storySpineResult.error) {
    throw mapLoreError(storySpineResult.error);
  }

  if (!governanceResult.data || !storySpineResult.data) {
    throw new LoreActionError("not-found", "Required lore documents are missing.");
  }

  return [
    buildProjectDocumentView("governance", governanceResult.data as GovernanceDocRecord),
    buildProjectDocumentView("story-spine", storySpineResult.data as StorySpineRecord),
  ];
}

export async function updateProjectDocument(
  projectId: string,
  kind: ProjectDocumentKind,
  input: UpdateProjectDocumentInput,
) {
  ensureConfigured();
  const validated = projectDocumentSchema.parse(input);

  if (kind === "governance") {
    const { data, error } = await supabase
      .from("governance_docs")
      .update({
        body: validated.body,
      })
      .eq("project_id", projectId)
      .select("*")
      .single();

    if (error) {
      throw mapLoreError(error);
    }

    return buildProjectDocumentView("governance", data as GovernanceDocRecord);
  }

  const { data, error } = await supabase
    .from("story_spines")
    .update({
      body: validated.body,
    })
    .eq("project_id", projectId)
    .select("*")
    .single();

  if (error) {
    throw mapLoreError(error);
  }

  return buildProjectDocumentView("story-spine", data as StorySpineRecord);
}

export async function listLoreEntries(
  projectId: string,
  filters?: Partial<LoreEntryFilters>,
): Promise<LoreEntryView[]> {
  ensureConfigured();

  let query = supabase
    .from("lore_entries")
    .select("*")
    .eq("project_id", projectId)
    .order("updated_at", { ascending: false });

  if (filters?.typeFilter) {
    query = query.eq("type", filters.typeFilter);
  }

  if (filters?.statusFilter) {
    query = query.eq("canon_status", filters.statusFilter);
  }

  const searchText = filters?.searchText?.trim();
  if (searchText) {
    query = query.ilike("title", `%${searchText}%`);
  }

  const { data, error } = await query;

  if (error) {
    throw mapLoreError(error);
  }

  const entries = await Promise.all(
    ((data as LoreEntryRecord[] | null) ?? []).map((record) => mapLoreEntryWithCover(record)),
  );

  return sortLoreEntriesByUpdatedAt(entries);
}

export async function getProjectLoreCapacity(
  projectId: string,
): Promise<ProjectLoreCapacitySummary> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_project_lore_capacity", {
    target_project_id: projectId,
  });

  if (error) {
    throw mapLoreError(error);
  }

  const capacityRow = data?.[0];

  if (!capacityRow) {
    throw new LoreActionError("not-found", "That project is unavailable.");
  }

  return mapLoreCapacityRow({
    tier: capacityRow.tier,
    lore_entries_used: capacityRow.lore_entries_used,
    lore_entries_limit: capacityRow.lore_entries_limit,
    lore_entries_remaining: capacityRow.lore_entries_remaining,
    is_unlimited: capacityRow.is_unlimited,
  });
}

export async function createLoreEntry(
  projectId: string,
  input: CreateLoreEntryInput,
): Promise<LoreEntryView> {
  ensureConfigured();
  const validated = createLoreEntrySchema.parse({
    ...input,
    canonStatus: "draft",
  });

  const body = buildLoreDocumentBody(validated.type, validated.body, validated.localRules);

  const { data, error } = await supabase
    .from("lore_entries")
    .insert({
      project_id: projectId,
      type: validated.type,
      title: validated.title,
      body,
      canon_status: "draft",
    })
    .select("*")
    .single();

  if (error) {
    throw mapLoreError(error);
  }

  let record = data as LoreEntryRecord;

  if (validated.coverImageFile) {
    try {
      const userId = await getCurrentUserId();
      const coverImagePath = await uploadLoreCover(
        userId,
        projectId,
        record.id,
        validated.coverImageFile,
      );

      const updateResult = await supabase
        .from("lore_entries")
        .update({
          cover_image_path: coverImagePath,
        })
        .eq("id", record.id)
        .select("*")
        .single();

      if (updateResult.error) {
        throw updateResult.error;
      }

      record = updateResult.data as LoreEntryRecord;
    } catch (coverError) {
      await supabase.from("lore_entries").delete().eq("id", record.id);
      throw mapLoreError(coverError);
    }
  }

  return mapLoreEntryWithCover(record);
}

export async function updateLoreEntry(
  entryId: string,
  input: UpdateLoreEntryInput,
): Promise<LoreEntryView> {
  ensureConfigured();
  const validated = updateLoreEntrySchema.parse(input);
  const existingEntry = await getOwnedLoreEntryById(entryId);

  if (!existingEntry) {
    throw new LoreActionError("not-found", "That lore entry is unavailable.");
  }

  const nextBody = buildLoreDocumentBody(
    validated.type,
    validated.body,
    validated.localRules,
  );

  let nextCoverImagePath = existingEntry.cover_image_path;
  let uploadedCoverPath: string | null = null;

  if (validated.coverImageFile) {
    const userId = await getCurrentUserId();
    uploadedCoverPath = await uploadLoreCover(
      userId,
      existingEntry.project_id,
      existingEntry.id,
      validated.coverImageFile,
    );
    nextCoverImagePath = uploadedCoverPath;
  } else if (validated.removeCoverImage) {
    nextCoverImagePath = null;
  }

  const { data, error } = await supabase
    .from("lore_entries")
    .update({
      title: validated.title,
      type: validated.type,
      body: nextBody,
      cover_image_path: nextCoverImagePath,
    })
    .eq("id", existingEntry.id)
    .select("*")
    .single();

  if (error) {
    if (uploadedCoverPath) {
      await deleteLoreCover(uploadedCoverPath).catch(() => undefined);
    }
    throw mapLoreError(error);
  }

  if (
    existingEntry.cover_image_path
    && (validated.removeCoverImage || uploadedCoverPath)
    && existingEntry.cover_image_path !== nextCoverImagePath
  ) {
    await deleteLoreCover(existingEntry.cover_image_path).catch(() => undefined);
  }

  return mapLoreEntryWithCover(data as LoreEntryRecord);
}

export async function updateLoreEntryCanonStatus(
  entryId: string,
  canonStatus: LoreCanonStatus,
): Promise<LoreEntryView> {
  ensureConfigured();

  const { data, error } = await supabase
    .from("lore_entries")
    .update({
      canon_status: canonStatus,
    })
    .eq("id", entryId)
    .select("*")
    .single();

  if (error) {
    throw mapLoreError(error);
  }

  return mapLoreEntryWithCover(data as LoreEntryRecord);
}

export async function deleteLoreEntry(entryId: string) {
  ensureConfigured();
  const existingEntry = await getOwnedLoreEntryById(entryId);

  if (!existingEntry) {
    throw new LoreActionError("not-found", "That lore entry is unavailable.");
  }

  const { error } = await supabase
    .from("lore_entries")
    .delete()
    .eq("id", entryId);

  if (error) {
    throw mapLoreError(error);
  }

  if (existingEntry.cover_image_path) {
    await deleteLoreCover(existingEntry.cover_image_path).catch(() => undefined);
  }

  return {
    deletedEntryId: entryId,
  };
}
